import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useToastNotification } from '@/contexts/ToastContext';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Calendar as CalendarIcon,
  RefreshCw,
  Globe,
  X,
  ChevronDown,
  Trash2,
  Users,
  Mail,
  Paperclip,
  Edit3,
  Clock,
  Repeat,
  Loader2,
  Bell,
  Link2,
  Settings,
  Check,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { CalendarDateTime } from '@/components/ui/calendar-datetime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Constants
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAYS_SHORT = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TIMEZONES = [
  { value: 'UTC', label: 'UTC', offset: '+0' },
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: '-5' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: '-6' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: '-7' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: '-8' },
  { value: 'Europe/London', label: 'London (GMT)', offset: '+0' },
  { value: 'Europe/Paris', label: 'Paris (CET)', offset: '+1' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: '+9' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', offset: '+8' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', offset: '+11' },
];

const REPEAT_OPTIONS = [
  { value: 'none', label: 'Never' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const REMINDER_OPTIONS = [
  { value: 5, label: '5 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
  { value: 2880, label: '2 days before' },
];

// Event colors
const EVENT_COLORS = [
  { bg: 'bg-green-500', dot: 'bg-green-500', text: 'text-white' },
  { bg: 'bg-blue-500', dot: 'bg-blue-500', text: 'text-white' },
  { bg: 'bg-purple-500', dot: 'bg-purple-500', text: 'text-white' },
  { bg: 'bg-red-500', dot: 'bg-red-500', text: 'text-white' },
  { bg: 'bg-orange-500', dot: 'bg-orange-500', text: 'text-white' },
  { bg: 'bg-cyan-500', dot: 'bg-cyan-500', text: 'text-white' },
  { bg: 'bg-pink-500', dot: 'bg-pink-500', text: 'text-white' },
];

// Helper functions
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
};

const formatTime = (date) => {
  const d = new Date(date);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false });
};

const formatTime12 = (date) => {
  const d = new Date(date);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDateLong = (date) => {
  const d = new Date(date);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
};

const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

const isToday = (date) => isSameDay(date, new Date());

const getEventColor = (eventId, event = null) => {
  // For external events, use the calendar color if available
  if (event?.isExternal && event?.calendarColor) {
    return {
      bg: '',
      dot: '',
      text: 'text-white',
      style: { backgroundColor: event.calendarColor }
    };
  }
  const hash = eventId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return EVENT_COLORS[hash % EVENT_COLORS.length];
};

const getTimezoneOffset = (tz) => {
  const found = TIMEZONES.find(t => t.value === tz);
  return found ? `GMT${found.offset}` : 'GMT+0';
};

// ===== Lightning-Fast Search Utilities =====
// Debounce utility for API calls
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

// Search cache for instant repeat queries
const searchCache = new Map();
const CACHE_TTL = 60000; // 1 minute cache

const getCachedSearch = (query) => {
  const cached = searchCache.get(query.toLowerCase());
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.results;
  }
  return null;
};

const setCachedSearch = (query, results) => {
  searchCache.set(query.toLowerCase(), { results, timestamp: Date.now() });
  // Clean old entries
  if (searchCache.size > 50) {
    const entries = Array.from(searchCache.entries());
    entries.slice(0, 25).forEach(([key]) => searchCache.delete(key));
  }
};

// Fast client-side search (instant filtering)
const searchEventsLocally = (events, query) => {
  if (!query.trim()) return null;
  const q = query.toLowerCase();
  return events.filter(event => 
    event.title?.toLowerCase().includes(q) ||
    event.location?.toLowerCase().includes(q) ||
    event.notes?.toLowerCase().includes(q)
  );
};

// ===== Event Details Popover Component =====
const EventDetailsPopover = ({ event, position, onClose, onEdit, onDelete, color, fromSearch }) => {
  const popoverRef = useRef(null);
  const [popoverDimensions, setPopoverDimensions] = useState({ width: 320, height: 400 });
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Measure popover after render
  useEffect(() => {
    if (popoverRef.current) {
      const rect = popoverRef.current.getBoundingClientRect();
      setPopoverDimensions({ width: rect.width, height: rect.height });
    }
  }, [event]);

  if (!event || !position) return null;

  const startDate = new Date(event.startTime);
  const endDate = new Date(event.endTime);
  const tzOffset = getTimezoneOffset(event.timezone);

  // Popover dimensions
  const popoverWidth = 320;
  const popoverHeight = popoverDimensions.height || 400;
  const arrowSize = 12;
  const gap = 8;

  // If opened from search, center the popover on screen (no arrow)
  const isCentered = fromSearch === true;

  let left, top, showOnLeft, arrowTop;

  if (isCentered) {
    // Center the popover on screen
    left = (window.innerWidth - popoverWidth) / 2;
    top = (window.innerHeight - popoverHeight) / 2;
    showOnLeft = false;
    arrowTop = 0;
  } else {
    // Determine if popover should appear on LEFT or RIGHT of the event
    // If event is in the right half of screen, show popover on left (arrow points right)
    // If event is in the left half of screen, show popover on right (arrow points left)
    const screenMidpoint = window.innerWidth / 2;
    showOnLeft = position.x > screenMidpoint;

    // Calculate horizontal position
    if (showOnLeft) {
      // Popover on LEFT of event, arrow points RIGHT
      left = position.x - popoverWidth - gap - arrowSize;
    } else {
      // Popover on RIGHT of event, arrow points LEFT
      left = position.x + position.width + gap + arrowSize;
    }

    // Ensure popover stays within screen bounds horizontally
    left = Math.max(20, Math.min(left, window.innerWidth - popoverWidth - 20));

    // Calculate vertical position - center the popover vertically on the event
    top = position.y + (position.height / 2) - (popoverHeight / 2);
    
    // Ensure popover stays within screen bounds vertically
    top = Math.max(20, Math.min(top, window.innerHeight - popoverHeight - 20));

    // Calculate arrow vertical position to point at the event
    // Arrow should be positioned relative to where the event center is
    const eventCenterY = position.y + (position.height / 2);
    arrowTop = Math.max(30, Math.min(eventCenterY - top - (arrowSize / 2), popoverHeight - 50));
  }

  const popoverStyle = {
    position: 'fixed',
    left,
    top,
    zIndex: 100,
  };

  return (
    <motion.div
      ref={popoverRef}
      initial={{ opacity: 0, scale: isCentered ? 0.95 : 1, x: isCentered ? 0 : (showOnLeft ? 10 : -10) }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: isCentered ? 0.95 : 1, x: isCentered ? 0 : (showOnLeft ? 10 : -10) }}
      transition={{ duration: 0.15 }}
      style={popoverStyle}
      className="w-80"
    >
      {/* Arrow pointing horizontally to the event - hidden when centered */}
      {!isCentered && (showOnLeft ? (
        // Arrow on RIGHT side of popover, pointing RIGHT towards event
        <div 
          className="absolute -right-2 w-4 h-4 bg-[#2c2c2e] rotate-45 border-r border-t border-zinc-700"
          style={{ top: arrowTop }}
        />
      ) : (
        // Arrow on LEFT side of popover, pointing LEFT towards event
        <div 
          className="absolute -left-2 w-4 h-4 bg-[#2c2c2e] rotate-45 border-l border-b border-zinc-700"
          style={{ top: arrowTop }}
        />
      ))}
      
      {/* Popover content */}
      <div className="bg-[#2c2c2e] rounded-xl border border-zinc-700 shadow-2xl overflow-hidden">
        {/* Header with title and colored dot */}
        <div className="p-4 border-b border-zinc-700">
          <div className="flex items-start gap-3">
            <div className={cn("w-3 h-3 rounded-full mt-1.5 flex-shrink-0", color.dot)} />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-white leading-tight">{event.title}</h3>
            </div>
            <button 
              onClick={onClose}
              className="p-1 text-zinc-400 hover:text-white transition-colors -mr-1"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Date and Time */}
        <div className="px-4 py-3 border-b border-zinc-700">
          <div className="flex items-center gap-2 text-white">
            <Clock size={14} className="text-zinc-500" />
            <span className="text-sm font-medium">{formatDateLong(startDate)}</span>
          </div>
          {event.allDay ? (
            <div className="mt-1 ml-6 text-sm text-zinc-400">All day</div>
          ) : (
            <div className="mt-1 ml-6 text-sm text-zinc-400">
              {formatTime12(startDate)} ({tzOffset}) – {formatTime12(endDate)} ({tzOffset})
            </div>
          )}
          {event.repeatRule && event.repeatRule !== 'none' && (
            <div className="mt-2 flex items-center gap-2 ml-6">
              <Repeat size={12} className="text-zinc-500" />
              <span className="text-xs text-zinc-500 capitalize">{event.repeatRule}</span>
            </div>
          )}
        </div>

        {/* Add Invitees */}
        <div className="px-4 py-3 border-b border-zinc-700">
          <div className="flex items-center gap-2 text-zinc-400 hover:text-cyan-400 cursor-pointer transition-colors">
            <Users size={14} />
            <span className="text-sm">
              {event.invitees && event.invitees.length > 0 
                ? event.invitees.join(', ') 
                : 'Add Invitees'}
            </span>
          </div>
        </div>

        {/* Notes/Details */}
        {event.notes && (
          <div className="px-4 py-3 border-b border-zinc-700">
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{event.notes}</p>
          </div>
        )}

        {/* Add to Calendar Links */}
        <div className="px-4 py-3 space-y-2 border-b border-zinc-700">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Add to Calendar</div>
          <a 
            href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${new Date(event.startTime).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}/${new Date(event.endTime).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}&details=${encodeURIComponent(event.notes || '')}&location=${encodeURIComponent(event.location || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm"
          >
            <CalendarIcon size={14} />
            Add to Google Calendar
          </a>
          <a 
            href={`${API_URL}/api/calendar/events/${event.id}/ics`}
            download
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
          >
            <CalendarIcon size={14} />
            Download for Apple/Outlook (.ics)
          </a>
        </div>

        {/* Notification Status */}
        {event.notificationEnabled && (
          <div className="px-4 py-2 border-b border-zinc-700">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Bell size={12} />
              Reminder: {event.reminderMinutes < 60 ? `${event.reminderMinutes} min` : `${event.reminderMinutes / 60}h`} before
              {event.notificationSent && <span className="text-green-500 ml-1">✓ Sent</span>}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="px-4 py-3 bg-[#1c1c1e] flex items-center justify-between border-t border-zinc-700">
          <button
            onClick={() => onDelete(event.id)}
            className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1 transition-colors"
          >
            <Trash2 size={14} />
            Delete
          </button>
          <button
            onClick={() => onEdit(event)}
            className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors"
          >
            <Edit3 size={14} />
            Edit
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ===== iOS-Style Form Row Component =====
const FormRow = ({ label, children, className }) => (
  <div className={cn("flex items-center justify-between py-3 px-4 border-b border-white/5 last:border-0", className)}>
    <span className="text-sm text-zinc-300">{label}</span>
    <div className="flex-1 ml-4 flex justify-end">{children}</div>
  </div>
);

// ===== Event Creation/Edit Modal (iOS-Style) =====
const EventModal = ({ open, onClose, event, onSave, onDelete, toast }) => {
  const isEditing = !!event?.id;
  
  const getDefaultEndTime = (start) => {
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    return end;
  };

  const [formData, setFormData] = useState({
    title: '',
    location: '',
    allDay: false,
    startTime: new Date(),
    endTime: getDefaultEndTime(new Date()),
    timezone: 'UTC',
    repeatRule: 'none',
    repeatEndDate: null,
    notes: '',
    invitees: '',
    calendarName: 'Shared Admin Calendar',
    notificationEnabled: true,
    reminderMinutes: 15,
  });
  
  const [showTimezone, setShowTimezone] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        location: event.location || '',
        allDay: event.allDay || false,
        startTime: event.startTime ? new Date(event.startTime) : new Date(),
        endTime: event.endTime ? new Date(event.endTime) : getDefaultEndTime(new Date()),
        timezone: event.timezone || 'UTC',
        repeatRule: event.repeatRule || 'none',
        repeatEndDate: event.repeatEndDate ? new Date(event.repeatEndDate) : null,
        notes: event.notes || '',
        invitees: event.invitees ? event.invitees.join(', ') : '',
        calendarName: event.calendarName || 'Shared Admin Calendar',
        notificationEnabled: event.notificationEnabled !== false,
        reminderMinutes: event.reminderMinutes || 15,
      });
    } else {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
      setFormData({
        title: '',
        location: '',
        allDay: false,
        startTime: now,
        endTime: getDefaultEndTime(now),
        timezone: 'UTC',
        repeatRule: 'none',
        repeatEndDate: null,
        notes: '',
        invitees: '',
        calendarName: 'Shared Admin Calendar',
        notificationEnabled: true,
        reminderMinutes: 15,
      });
    }
  }, [event, open]);

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        location: formData.location || null,
        allDay: formData.allDay,
        startTime: formData.startTime.toISOString(),
        endTime: formData.endTime.toISOString(),
        timezone: formData.timezone,
        repeatRule: formData.repeatRule === 'none' ? null : formData.repeatRule,
        repeatEndDate: formData.repeatEndDate ? formData.repeatEndDate.toISOString() : null,
        notes: formData.notes || null,
        invitees: formData.invitees ? formData.invitees.split(',').map(e => e.trim()).filter(Boolean) : [],
        calendarName: formData.calendarName,
        notificationEnabled: formData.notificationEnabled,
        reminderMinutes: formData.reminderMinutes,
      };

      await onSave(payload, event?.id);
      onClose();
    } catch (error) {
      toast.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'startTime' && value > prev.endTime) {
        updated.endTime = getDefaultEndTime(value);
      }
      if (field === 'allDay' && value) {
        const start = new Date(prev.startTime);
        start.setHours(0, 0, 0, 0);
        const end = new Date(prev.startTime);
        end.setHours(23, 59, 59, 999);
        updated.startTime = start;
        updated.endTime = end;
      }
      return updated;
    });
  };

  const formatDateTimeLocal = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const formatDateLocal = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 bg-[#1c1c1e] border-white/10 overflow-hidden">
        {/* iOS-style header */}
        <div className="flex items-center justify-center py-4 border-b border-white/10 bg-[#2c2c2e]">
          <div className="inline-flex bg-[#3a3a3c] rounded-full p-1">
            <button className="px-6 py-1.5 text-sm font-medium rounded-full bg-red-500 text-white">
              Event
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* Title */}
          <div className="bg-[#2c2c2e] rounded-xl px-4 py-3">
            <Input
              placeholder="New Event"
              value={formData.title}
              onChange={(e) => updateField('title', e.target.value)}
              className="bg-transparent border-0 text-lg font-medium text-white placeholder:text-zinc-500 p-0 h-auto focus-visible:ring-0"
            />
          </div>

          {/* Location */}
          <div className="bg-[#2c2c2e] rounded-xl">
            <FormRow label="Add Location">
              <Input
                placeholder="Location or Video Call"
                value={formData.location}
                onChange={(e) => updateField('location', e.target.value)}
                className="bg-transparent border-0 text-sm text-right text-zinc-300 placeholder:text-zinc-600 p-0 h-auto focus-visible:ring-0 max-w-[180px]"
              />
            </FormRow>
          </div>

          {/* Date & Time */}
          <div className="bg-[#2c2c2e] rounded-xl">
            <FormRow label="All Day">
              <Switch
                checked={formData.allDay}
                onCheckedChange={(checked) => updateField('allDay', checked)}
              />
            </FormRow>
            <FormRow label="Starts">
              <CalendarDateTime
                value={formData.startTime}
                onChange={(date) => updateField('startTime', date)}
                placeholder="Select date & time"
                showTimeInput={!formData.allDay}
                className="w-auto h-8 text-xs"
              />
            </FormRow>
            <FormRow label="Ends">
              <CalendarDateTime
                value={formData.endTime}
                onChange={(date) => updateField('endTime', date)}
                placeholder="Select date & time"
                showTimeInput={!formData.allDay}
                className="w-auto h-8 text-xs"
              />
            </FormRow>
            <FormRow label="Repeat">
              <Select value={formData.repeatRule} onValueChange={(v) => updateField('repeatRule', v)}>
                <SelectTrigger className="w-auto bg-transparent border-0 text-sm text-zinc-300 p-0 h-auto gap-1 focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2c2c2e] border-white/10">
                  {REPEAT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-zinc-300">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormRow>
            <button
              onClick={() => setShowTimezone(!showTimezone)}
              className="w-full flex items-center justify-between py-3 px-4 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Globe size={14} />
                Time Zone
              </span>
              <ChevronDown size={14} className={cn("transition-transform", showTimezone && "rotate-180")} />
            </button>
            {showTimezone && (
              <FormRow label="Time Zone" className="pt-0">
                <Select value={formData.timezone} onValueChange={(v) => updateField('timezone', v)}>
                  <SelectTrigger className="w-auto bg-transparent border-0 text-sm text-zinc-300 p-0 h-auto gap-1 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2c2c2e] border-white/10 max-h-[200px]">
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value} className="text-zinc-300">
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormRow>
            )}
          </div>

          {/* Invitees */}
          <div className="bg-[#2c2c2e] rounded-xl">
            <FormRow label="Add Invitees">
              <Input
                placeholder="email@example.com"
                value={formData.invitees}
                onChange={(e) => updateField('invitees', e.target.value)}
                className="bg-transparent border-0 text-sm text-right text-zinc-300 placeholder:text-zinc-600 p-0 h-auto focus-visible:ring-0 max-w-[180px]"
              />
            </FormRow>
          </div>

          {/* Notification Settings */}
          <div className="bg-[#2c2c2e] rounded-xl">
            <FormRow label="Email Reminder">
              <Switch
                checked={formData.notificationEnabled}
                onCheckedChange={(checked) => updateField('notificationEnabled', checked)}
              />
            </FormRow>
            {formData.notificationEnabled && (
              <FormRow label="Remind me">
                <Select value={String(formData.reminderMinutes)} onValueChange={(v) => updateField('reminderMinutes', parseInt(v))}>
                  <SelectTrigger className="w-auto bg-transparent border-0 text-sm text-zinc-300 p-0 h-auto gap-1 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2c2c2e] border-white/10">
                    {REMINDER_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)} className="text-zinc-300">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormRow>
            )}
            <div className="px-4 py-2 text-xs text-zinc-500 border-t border-white/5">
              <Bell size={12} className="inline mr-1" />
              Sends email to you and all invitees
            </div>
          </div>

          {/* Notes */}
          <div className="bg-[#2c2c2e] rounded-xl px-4 py-3">
            <Textarea
              placeholder="Add Notes, URL, or Attachments"
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              className="bg-transparent border-0 text-sm text-zinc-300 placeholder:text-zinc-500 p-0 min-h-[60px] resize-none focus-visible:ring-0"
            />
          </div>

          {/* Calendar */}
          <div className="bg-[#2c2c2e] rounded-xl">
            <FormRow label="Calendar">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm text-zinc-300">{formData.calendarName}</span>
              </div>
            </FormRow>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-[#1c1c1e]">
          {isEditing ? (
            <Button
              variant="ghost"
              onClick={() => onDelete(event.id)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 size={16} className="mr-2" />
              Delete
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose} className="text-zinc-400">
              Cancel
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || !formData.title.trim()}
            className="bg-red-500 hover:bg-red-600 text-white px-6"
          >
            {saving ? 'Saving...' : isEditing ? 'Update' : 'Add'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== Mini Calendar Component =====
const MiniCalendar = ({ currentDate, onDateSelect, events }) => {
  const [viewDate, setViewDate] = useState(currentDate);
  
  useEffect(() => {
    setViewDate(currentDate);
  }, [currentDate]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  
  const prevMonthDays = getDaysInMonth(year, month - 1);
  const prevFill = Array.from({ length: firstDay }, (_, i) => prevMonthDays - firstDay + i + 1);
  const currentDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
  const nextFill = Array.from({ length: totalCells - firstDay - daysInMonth }, (_, i) => i + 1);

  const navigateMonth = (dir) => {
    setViewDate(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + dir);
      return d;
    });
  };

  return (
    <div className="bg-[#1c1c1e] rounded-lg p-3">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigateMonth(-1)} className="p-1 text-zinc-500 hover:text-white">
          <ChevronLeft size={14} />
        </button>
        <span className="text-xs font-medium text-white">
          {MONTHS[month]} {year}
        </span>
        <button onClick={() => navigateMonth(1)} className="p-1 text-zinc-500 hover:text-white">
          <ChevronRight size={14} />
        </button>
      </div>
      
      <div className="grid grid-cols-7 mb-1">
        {DAYS_SHORT.map((day, i) => (
          <div key={i} className="w-6 h-6 flex items-center justify-center text-[10px] text-zinc-500">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7">
        {prevFill.map(day => (
          <div key={`prev-${day}`} className="w-6 h-6 flex items-center justify-center text-[10px] text-zinc-700">
            {day}
          </div>
        ))}
        {currentDays.map(day => {
          const date = new Date(year, month, day);
          const isCurrentDay = isToday(date);
          const isSelected = isSameDay(date, currentDate);
          
          return (
            <button
              key={`curr-${day}`}
              onClick={() => onDateSelect(date)}
              className={cn(
                "w-6 h-6 flex items-center justify-center text-[10px] rounded-full transition-colors",
                isCurrentDay && "text-red-500 font-medium",
                isSelected && !isCurrentDay && "bg-zinc-700 text-white",
                !isCurrentDay && !isSelected && "text-white hover:bg-zinc-800"
              )}
            >
              {day}
            </button>
          );
        })}
        {nextFill.map(day => (
          <div key={`next-${day}`} className="w-6 h-6 flex items-center justify-center text-[10px] text-zinc-700">
            {day}
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Event Pill Component with Click Handler =====
const EventPill = ({ event, onEventClick }) => {
  const color = getEventColor(event.id);
  const startTime = formatTime(event.startTime);
  
  const handleClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    // Pass full bounding rect info for proper popover positioning
    onEventClick(event, { 
      x: rect.left, 
      y: rect.top, 
      width: rect.width, 
      height: rect.height,
      right: rect.right,
      bottom: rect.bottom
    });
  };

  return (
    <div
      onClick={handleClick}
      data-event-id={event.id}
      className={cn(
        "flex items-center gap-1 px-1 py-0.5 rounded text-[10px] truncate cursor-pointer hover:opacity-80 transition-opacity",
        color.bg
      )}
    >
      {event.allDay ? (
        <span className="truncate font-medium">{event.title}</span>
      ) : (
        <>
          <span className="text-white/70 flex-shrink-0">{startTime}</span>
          <span className="truncate font-medium">{event.title}</span>
        </>
      )}
    </div>
  );
};

// ===== Full Page Month View =====
const FullMonthView = ({ currentDate, events, onEventClick, onDateSelect, onQuickCreate }) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  
  const prevMonthDays = getDaysInMonth(year, month - 1);
  const prevFill = Array.from({ length: firstDay }, (_, i) => ({
    day: prevMonthDays - firstDay + i + 1,
    isCurrentMonth: false,
    date: new Date(year, month - 1, prevMonthDays - firstDay + i + 1)
  }));
  
  const currentDays = Array.from({ length: daysInMonth }, (_, i) => ({
    day: i + 1,
    isCurrentMonth: true,
    date: new Date(year, month, i + 1)
  }));
  
  const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
  const nextFill = Array.from({ length: totalCells - firstDay - daysInMonth }, (_, i) => ({
    day: i + 1,
    isCurrentMonth: false,
    date: new Date(year, month + 1, i + 1)
  }));

  const allDays = [...prevFill, ...currentDays, ...nextFill];
  const weeks = [];
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push(allDays.slice(i, i + 7));
  }

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventStart = new Date(event.startTime);
      const eventEnd = new Date(event.endTime);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      return eventStart <= dayEnd && eventEnd >= dayStart;
    });
  };

  return (
    <div className="flex-1 flex flex-col bg-[#1c1c1e]">
      <div className="grid grid-cols-7 border-b border-zinc-800">
        {DAYS.map(day => (
          <div key={day} className="py-2 text-center text-xs font-medium text-zinc-500 border-r border-zinc-800 last:border-r-0">
            {day}
          </div>
        ))}
      </div>
      
      <div className="flex-1 grid grid-rows-6">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 border-b border-zinc-800 last:border-b-0">
            {week.map((dayInfo, dayIdx) => {
              const dayEvents = getEventsForDay(dayInfo.date);
              const isCurrentDay = isToday(dayInfo.date);
              
              return (
                <div
                  key={dayIdx}
                  onClick={() => onDateSelect(dayInfo.date)}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    onQuickCreate && onQuickCreate(dayInfo.date);
                  }}
                  className={cn(
                    "min-h-[120px] p-1 border-r border-zinc-800 last:border-r-0 cursor-pointer hover:bg-zinc-900/50 transition-colors",
                    !dayInfo.isCurrentMonth && "bg-[#151517]"
                  )}
                >
                  {/* Day number */}
                  <div className="flex items-start mb-1">
                    <span className={cn(
                      "text-sm font-medium",
                      isCurrentDay ? "text-red-500" : dayInfo.isCurrentMonth ? "text-white" : "text-zinc-600",
                    )}>
                      {dayInfo.day === 1 && dayInfo.isCurrentMonth && (
                        <span className="text-zinc-500 mr-1">{MONTHS[month].slice(0, 3)}</span>
                      )}
                      {dayInfo.day}
                    </span>
                  </div>
                  
                  <div className="space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 4).map((event) => (
                      <EventPill key={event.id} event={event} onEventClick={onEventClick} />
                    ))}
                    {dayEvents.length > 4 && (
                      <div className="text-[10px] text-zinc-500 px-1">+{dayEvents.length - 4} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Day View =====
const DayView = ({ currentDate, events, onEventClick }) => {
  const HOURS = Array.from({ length: 24 }, (_, i) => i);
  const HOUR_HEIGHT = 60;
  const TIME_COL_WIDTH = 60;
  
  const dayEvents = events.filter(event => {
    const eventStart = new Date(event.startTime);
    const eventEnd = new Date(event.endTime);
    const dayStart = new Date(currentDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(currentDate);
    dayEnd.setHours(23, 59, 59, 999);
    return eventStart <= dayEnd && eventEnd >= dayStart;
  });

  const allDayEvents = dayEvents.filter(e => e.allDay);
  const timedEvents = dayEvents.filter(e => !e.allDay);

  const getEventPosition = (event) => {
    const start = new Date(event.startTime);
    const end = new Date(event.endTime);
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    const top = startHour * HOUR_HEIGHT;
    const height = Math.max((endHour - startHour) * HOUR_HEIGHT, 30);
    return { top, height };
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    onEventClick(event, { 
      x: rect.left, 
      y: rect.top, 
      width: rect.width, 
      height: rect.height,
      right: rect.right,
      bottom: rect.bottom
    });
  };

  return (
    <div className="flex-1 bg-[#1c1c1e] flex flex-col h-full">
      {allDayEvents.length > 0 && (
        <div className="border-b border-zinc-800 p-2 bg-[#2c2c2e] flex-shrink-0">
          <div className="text-xs text-zinc-500 mb-2">All-day</div>
          <div className="space-y-1">
            {allDayEvents.map(event => {
              const color = getEventColor(event.id);
              return (
                <div
                  key={event.id}
                  data-event-id={event.id}
                  onClick={(e) => handleEventClick(event, e)}
                  className={cn("px-2 py-1 rounded text-sm cursor-pointer hover:opacity-80", color.bg)}
                >
                  {event.title}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scrollable time grid */}
      <div 
        className="flex-1 min-h-0 day-scroll-container"
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <style>{`
          .day-scroll-container::-webkit-scrollbar {
            display: none;
          }
          .day-scroll-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
        <div style={{ height: HOURS.length * HOUR_HEIGHT + 20, position: 'relative', paddingTop: 10 }}>
          {/* Time labels */}
          {HOURS.map(hour => (
            <div 
              key={`time-${hour}`}
              className="absolute text-xs text-zinc-500 text-right pr-2"
              style={{ 
                top: hour * HOUR_HEIGHT + 4, 
                left: 0, 
                width: TIME_COL_WIDTH 
              }}
            >
              {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
            </div>
          ))}

          {/* Hour grid lines */}
          {HOURS.map(hour => (
            <div
              key={`line-${hour}`}
              className="absolute border-t border-zinc-800/50"
              style={{ 
                top: hour * HOUR_HEIGHT + 10, 
                left: TIME_COL_WIDTH, 
                right: 0 
              }}
            />
          ))}

          {/* Events container */}
          <div 
            className="absolute"
            style={{ left: TIME_COL_WIDTH + 8, right: 16, top: 10, bottom: 0 }}
          >
            {timedEvents.map(event => {
              const { top, height } = getEventPosition(event);
              const color = getEventColor(event.id);
              return (
                <div
                  key={event.id}
                  data-event-id={event.id}
                  onClick={(e) => handleEventClick(event, e)}
                  className={cn("absolute left-0 right-0 rounded px-2 py-1 cursor-pointer hover:opacity-80 overflow-hidden", color.bg)}
                  style={{ top, height, minHeight: 30 }}
                >
                  <div className="text-sm font-medium text-white truncate">{event.title}</div>
                  <div className="text-xs text-white/70">
                    {formatTime(event.startTime)} - {formatTime(event.endTime)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Current time indicator */}
          {isToday(currentDate) && (
            <div
              className="absolute left-0 right-0 border-t-2 border-red-500 z-10"
              style={{ top: (new Date().getHours() + new Date().getMinutes() / 60) * HOUR_HEIGHT + 10 }}
            >
              <div className="absolute -left-1 -top-1.5 w-3 h-3 bg-red-500 rounded-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== Week View =====
const WeekView = ({ currentDate, events, onEventClick, onDateSelect }) => {
  const startOfWeek = new Date(currentDate);
  const dayOfWeek = startOfWeek.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  startOfWeek.setDate(startOfWeek.getDate() + diff);
  
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek);
    date.setDate(date.getDate() + i);
    return date;
  });

  const HOURS = Array.from({ length: 24 }, (_, i) => i);
  const TIME_COL_WIDTH = 60;
  const HOUR_HEIGHT = 60;

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventStart = new Date(event.startTime);
      const eventEnd = new Date(event.endTime);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      return eventStart <= dayEnd && eventEnd >= dayStart;
    });
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    onEventClick(event, { 
      x: rect.left, 
      y: rect.top, 
      width: rect.width, 
      height: rect.height,
      right: rect.right,
      bottom: rect.bottom
    });
  };

  return (
    <div className="flex-1 bg-[#1c1c1e] flex flex-col h-full">
      {/* Fixed Header row with days */}
      <div className="flex border-b border-zinc-800 flex-shrink-0">
        <div className="flex-shrink-0" style={{ width: TIME_COL_WIDTH }} />
        {weekDays.map((date, i) => {
          const isCurrentDay = isToday(date);
          return (
            <div
              key={i}
              onClick={() => onDateSelect(date)}
              className="flex-1 py-3 text-center border-l border-zinc-800 cursor-pointer hover:bg-zinc-900/50"
            >
              <div className="text-xs text-zinc-500">{DAYS[i]}</div>
              <div className={cn(
                "text-xl font-semibold mt-1",
                isCurrentDay ? "text-red-500" : "text-white"
              )}>
                {date.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scrollable time grid */}
      <div 
        className="flex-1 min-h-0 week-scroll-container"
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <style>{`
          .week-scroll-container::-webkit-scrollbar {
            display: none;
          }
          .week-scroll-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
        <div style={{ height: HOURS.length * HOUR_HEIGHT + 20, position: 'relative', paddingTop: 10 }}>
          {/* Time labels */}
          {HOURS.map(hour => (
            <div 
              key={`time-${hour}`}
              className="absolute text-xs text-zinc-500 text-right pr-2"
              style={{ 
                top: hour * HOUR_HEIGHT + 4, 
                left: 0, 
                width: TIME_COL_WIDTH 
              }}
            >
              {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
            </div>
          ))}

          {/* Hour grid lines */}
          {HOURS.map(hour => (
            <div
              key={`line-${hour}`}
              className="absolute border-t border-zinc-800/50"
              style={{ 
                top: hour * HOUR_HEIGHT + 10, 
                left: TIME_COL_WIDTH, 
                right: 0 
              }}
            />
          ))}

          {/* Day columns */}
          <div 
            className="absolute bottom-0 flex"
            style={{ left: TIME_COL_WIDTH, right: 0, top: 10 }}
          >
            {weekDays.map((date, dayIdx) => {
              const dayEvents = getEventsForDay(date).filter(e => !e.allDay);
              return (
                <div 
                  key={dayIdx} 
                  className="flex-1 relative border-l border-zinc-800"
                >
                  {dayEvents.map(event => {
                    const start = new Date(event.startTime);
                    const end = new Date(event.endTime);
                    const startHour = start.getHours() + start.getMinutes() / 60;
                    const endHour = end.getHours() + end.getMinutes() / 60;
                    const top = startHour * HOUR_HEIGHT;
                    const height = Math.max((endHour - startHour) * HOUR_HEIGHT, 30);
                    const color = getEventColor(event.id);
                    
                    return (
                      <div
                        key={event.id}
                        data-event-id={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className={cn("absolute left-0.5 right-0.5 rounded px-1.5 py-0.5 cursor-pointer hover:opacity-80 overflow-hidden text-[10px]", color.bg)}
                        style={{ top, height }}
                      >
                        <div className="font-medium truncate">{event.title}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Main Calendar Component =====
const Calendar = () => {
  const toast = useToastNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Calendar connections state
  const [connections, setConnections] = useState([]);
  const [syncedEvents, setSyncedEvents] = useState([]);
  const [showConnectionsPanel, setShowConnectionsPanel] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Lightning-fast search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 200); // 200ms debounce for API
  
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  
  // Event details popover state
  const [popoverEvent, setPopoverEvent] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [popoverFromSearch, setPopoverFromSearch] = useState(false);
  
  // Scroll navigation ref and state
  const calendarContainerRef = useRef(null);
  const scrollAccumulator = useRef(0);
  const lastNavigateTime = useRef(0);
  const SCROLL_THRESHOLD = 150; // Increased - pixels needed to trigger navigation
  const SCROLL_COOLDOWN = 600; // Increased cooldown between navigations
  
  // Animation state for scroll transition
  const [scrollDirection, setScrollDirection] = useState(null);

  // Check for OAuth callback
  useEffect(() => {
    const connected = searchParams.get('connected');
    if (connected === 'google') {
      toast.success('Google Calendar connected!');
      fetchConnections();
      searchParams.delete('connected');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, toast]);

  // Fetch calendar connections (silently fails on 401 - handled by auth context)
  const fetchConnections = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/calendar/connections`, { withCredentials: true });
      setConnections(response.data);
    } catch (error) {
      // Silently ignore 401 errors - session issues handled by auth context
      if (error.response?.status !== 401) {
        console.error('Error fetching connections:', error);
      }
    }
  }, []);

  // Fetch synced events from external calendars (silently fails on 401)
  const fetchSyncedEvents = useCallback(async () => {
    try {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
      
      const response = await axios.get(`${API_URL}/api/calendar/synced-events`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString()
        },
        withCredentials: true
      });
      setSyncedEvents(response.data);
    } catch (error) {
      // Silently ignore 401 errors - session issues handled by auth context
      if (error.response?.status !== 401) {
        console.error('Error fetching synced events:', error);
      }
    }
  }, [currentDate]);

  // Connect to Google Calendar
  const connectGoogleCalendar = async () => {
    setConnectingProvider('google');
    try {
      const response = await axios.get(`${API_URL}/api/calendar/oauth/google/authorize`, { withCredentials: true });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to connect Google Calendar');
      }
      setConnectingProvider(null);
    }
  };

  // Fetch available calendars for a connection
  const fetchAvailableCalendars = async (connectionId) => {
    setLoadingCalendars(true);
    try {
      const response = await axios.get(`${API_URL}/api/calendar/connections/${connectionId}/calendars`, { withCredentials: true });
      setAvailableCalendars(response.data);
    } catch (error) {
      console.error('Error fetching calendars:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to fetch calendars');
      }
    } finally {
      setLoadingCalendars(false);
    }
  };

  // Update selected calendars
  const updateSelectedCalendars = async (connectionId, calendarIds) => {
    try {
      await axios.put(`${API_URL}/api/calendar/connections/${connectionId}/calendars`, calendarIds, { withCredentials: true });
      toast.success('Calendars updated');
      fetchConnections();
    } catch (error) {
      console.error('Error updating calendars:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to update calendars');
      }
    }
  };

  // Sync calendar
  const syncCalendar = async (connectionId, silent = false) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/calendar/connections/${connectionId}/sync`, {}, { withCredentials: true });
      if (!silent) {
        toast.success(`Synced ${response.data.synced} events`);
      }
      fetchSyncedEvents();
      fetchConnections();
      return response.data.synced;
    } catch (error) {
      console.error('Error syncing:', error);
      if (error.response?.status !== 401 && !silent) {
        toast.error('Failed to sync calendar');
      }
      return 0;
    } finally {
      setSyncing(false);
    }
  };

  // Disconnect calendar
  const disconnectCalendar = async (connectionId) => {
    try {
      await axios.delete(`${API_URL}/api/calendar/connections/${connectionId}`, { withCredentials: true });
      toast.success('Calendar disconnected');
      setSelectedConnection(null);
      fetchConnections();
      fetchSyncedEvents();
    } catch (error) {
      console.error('Error disconnecting:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to disconnect calendar');
      }
    }
  };

  // Load connections on mount
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Fetch synced events when connections change
  useEffect(() => {
    if (connections.length > 0) {
      fetchSyncedEvents();
    }
  }, [connections, fetchSyncedEvents]);

  // INSTANT local search results (no debounce, filters loaded events immediately)
  const instantSearchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    return searchEventsLocally(events, searchQuery);
  }, [events, searchQuery]);

  const fetchEvents = useCallback(async () => {
    try {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
      
      const response = await axios.get(`${API_URL}/api/calendar/events`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        },
        withCredentials: true,
      });
      setEvents(response.data);
    } catch (error) {
      // Silently ignore 401 errors - session issues handled by auth context
      if (error.response?.status !== 401) {
        console.error('Failed to fetch events:', error);
        toast.error('Failed to load calendar events');
      }
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Auto-sync calendars every 1 minute when there are connected calendars
  useEffect(() => {
    if (connections.length === 0) return;
    
    // Set up interval for auto-sync every 60 seconds
    const syncInterval = setInterval(async () => {
      console.log('🔄 Auto-syncing calendars...');
      
      // Silent sync all connected calendars
      for (const conn of connections) {
        if (conn.syncEnabled) {
          try {
            await axios.post(`${API_URL}/api/calendar/connections/${conn.id}/sync`, {}, { withCredentials: true });
          } catch (error) {
            // Silently fail for background sync
            console.log('Background sync failed for', conn.email);
          }
        }
      }
      
      // Refresh events after background sync
      fetchSyncedEvents();
      fetchEvents();
    }, 60000); // 60000ms = 1 minute
    
    return () => clearInterval(syncInterval);
  }, [connections, fetchSyncedEvents, fetchEvents]);

  // Keyboard shortcut: Cmd/Ctrl+K to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);

  // Debounced API search (comprehensive server search after typing stops)
  useEffect(() => {
    const performServerSearch = async () => {
      if (!debouncedSearchQuery.trim()) {
        setSearchResults(null);
        setIsSearching(false);
        return;
      }

      // Check cache first
      const cached = getCachedSearch(debouncedSearchQuery);
      if (cached) {
        setSearchResults(cached);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await axios.get(`${API_URL}/api/calendar/search`, {
          params: { q: debouncedSearchQuery },
          withCredentials: true,
        });
        setCachedSearch(debouncedSearchQuery, response.data);
        setSearchResults(response.data);
      } catch (error) {
        // Silently ignore 401 errors - session issues handled by auth context
        if (error.response?.status !== 401) {
          console.error('Search failed:', error);
        }
      } finally {
        setIsSearching(false);
      }
    };

    performServerSearch();
  }, [debouncedSearchQuery]);

  // Handle search input change (instant UI update)
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.trim()) {
      setIsSearching(true); // Show loading while typing
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setIsSearching(false);
    searchInputRef.current?.focus();
  };

  const goToToday = () => setCurrentDate(new Date());
  
  const navigate = useCallback((direction, animated = false) => {
    if (animated) {
      setScrollDirection(direction);
    }
    
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (view === 'month') {
        d.setMonth(d.getMonth() + direction);
      } else if (view === 'week') {
        d.setDate(d.getDate() + (direction * 7));
      } else if (view === 'year') {
        d.setFullYear(d.getFullYear() + direction);
      } else {
        d.setDate(d.getDate() + direction);
      }
      return d;
    });
    
    if (animated) {
      // Reset animation direction after animation completes
      setTimeout(() => setScrollDirection(null), 250);
    }
  }, [view]);

  // Handle scroll-based navigation (like Apple Calendar)
  // Only enabled for Month and Year views - Week/Day views need vertical scroll for hours
  const handleCalendarScroll = useCallback((e) => {
    // Disable scroll navigation for week and day views - they need vertical scroll for hours
    if (view === 'week' || view === 'day') {
      return false; // Allow normal scroll
    }
    
    const now = Date.now();
    
    // Check cooldown - prevent navigation if too soon after last one
    if (now - lastNavigateTime.current < SCROLL_COOLDOWN) {
      return true;
    }
    
    // Accumulate scroll delta
    scrollAccumulator.current += e.deltaY;
    
    // Check if we've accumulated enough scroll to navigate
    if (Math.abs(scrollAccumulator.current) >= SCROLL_THRESHOLD) {
      const direction = scrollAccumulator.current > 0 ? 1 : -1;
      navigate(direction, true);
      scrollAccumulator.current = 0;
      lastNavigateTime.current = now;
    }
    
    // Decay the accumulator over time to prevent buildup
    setTimeout(() => {
      scrollAccumulator.current = scrollAccumulator.current * 0.5;
    }, 100);
    
    return true;
  }, [view, navigate]);

  // Attach scroll listener
  useEffect(() => {
    const container = calendarContainerRef.current;
    if (container) {
      const handleWheel = (e) => {
        // Only handle vertical scroll for month/year views
        if (view === 'week' || view === 'day') {
          // Let the scroll happen naturally for week/day views
          return;
        }
        
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          handleCalendarScroll(e);
        }
      };
      
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleCalendarScroll, view]);

  const handleDateSelect = (date) => {
    setCurrentDate(date);
    if (view === 'month') {
      setView('day');
    }
  };

  // Handle event click - show popover
  const handleEventClick = (event, position, fromSearch = false) => {
    setPopoverEvent(event);
    setPopoverPosition(position);
    setPopoverFromSearch(fromSearch);
  };

  // Handle search result click - navigate to event date and show popover on actual event
  const handleSearchResultClick = (event) => {
    const eventDate = new Date(event.startTime);
    
    // Close search popover
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults(null);
    
    // Navigate calendar to the event's date
    setCurrentDate(eventDate);
    
    // Wait for calendar to re-render, then find and click the event element
    setTimeout(() => {
      // Try to find the event element by its data-event-id attribute
      const eventElement = document.querySelector(`[data-event-id="${event.id}"]`);
      
      if (eventElement) {
        // Get the position of the actual event element
        const rect = eventElement.getBoundingClientRect();
        handleEventClick(event, {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
          right: rect.right,
          bottom: rect.bottom
        }, false); // fromSearch=false so it shows with arrow
      } else {
        // Fallback: show centered popover if element not found
        handleEventClick(event, {
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
          width: 0,
          height: 0,
          right: window.innerWidth / 2,
          bottom: window.innerHeight / 2
        }, true);
      }
    }, 150); // Small delay for calendar to re-render
  };

  // Close popover
  const closePopover = () => {
    setPopoverEvent(null);
    setPopoverPosition(null);
    setPopoverFromSearch(false);
  };

  // Edit from popover
  const handleEditFromPopover = (event) => {
    closePopover();
    setSelectedEvent(event);
    setEventModalOpen(true);
  };

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setEventModalOpen(true);
  };

  // Quick create event on double-click with pre-filled date
  const handleQuickCreate = (date) => {
    const start = new Date(date);
    start.setHours(9, 0, 0, 0); // Default to 9 AM
    const end = new Date(start);
    end.setHours(10, 0, 0, 0); // 1 hour duration
    
    setSelectedEvent({
      startTime: start.toISOString(),
      endTime: end.toISOString(),
    });
    setEventModalOpen(true);
  };

  const handleSaveEvent = async (eventData, eventId) => {
    try {
      if (eventId) {
        await axios.put(`${API_URL}/api/calendar/events/${eventId}`, eventData, { withCredentials: true });
        toast.success('Event updated');
      } else {
        await axios.post(`${API_URL}/api/calendar/events`, eventData, { withCredentials: true });
        toast.success('Event created');
      }
      fetchEvents();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteEvent = (eventId) => {
    setEventToDelete(eventId);
    setDeleteDialogOpen(true);
    closePopover();
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/calendar/events/${eventToDelete}`, { withCredentials: true });
      toast.success('Event deleted');
      setDeleteDialogOpen(false);
      setEventModalOpen(false);
      setEventToDelete(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Final display events - combines CRM events + synced external events + respects search filter
  const finalDisplayEvents = useMemo(() => {
    const allEvents = [...events, ...syncedEvents.map(e => ({
      ...e,
      isExternal: true,
      calendarName: e.calendarName || 'External Calendar',
      backgroundColor: e.calendarColor || '#6366f1'
    }))];
    return searchResults !== null ? searchResults : allEvents;
  }, [events, syncedEvents, searchResults]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 lg:left-16 flex flex-col bg-[#1c1c1e]"
    >
      {/* Top toolbar - full width */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#2c2c2e] border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <button className="p-2 text-zinc-400 hover:text-white transition-colors" onClick={fetchEvents}>
            <RefreshCw size={18} />
          </button>
          <button
            onClick={handleCreateEvent}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
            title="Create new event"
          >
            <Plus size={18} />
          </button>
        </div>

        <div className="flex items-center bg-[#3a3a3c] rounded-md p-0.5">
          {['Day', 'Week', 'Month', 'Year'].map((v) => (
            <button
              key={v}
              onClick={() => setView(v.toLowerCase())}
              className={cn(
                "px-4 py-1 text-xs font-medium rounded transition-colors",
                view === v.toLowerCase()
                  ? "bg-zinc-600 text-white"
                  : "text-zinc-400 hover:text-white"
              )}
            >
              {v}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Popover open={searchOpen} onOpenChange={(open) => {
            setSearchOpen(open);
            if (!open) {
              // Reset search when closing
              setSearchQuery('');
              setSearchResults(null);
              setIsSearching(false);
            }
          }}>
            <PopoverTrigger asChild>
              <button 
                className="p-2 text-zinc-400 hover:text-white transition-colors relative group"
                title="Search (⌘K)"
              >
                <Search size={18} />
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  ⌘K
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-96 bg-[#2c2c2e] border-zinc-700 p-3" align="end">
              {/* Search Input */}
              <div className="relative">
                {isSearching ? (
                  <Loader2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 animate-spin" />
                ) : (
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                )}
                <Input
                  ref={searchInputRef}
                  placeholder="Search events... (instant)"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-9 pr-8 bg-[#1c1c1e] border-zinc-700 text-sm focus:border-blue-500 transition-colors"
                  autoFocus
                />
                {searchQuery && (
                  <button 
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Instant Results (from local search) */}
              {searchQuery.trim() && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">
                      {instantSearchResults?.length || 0} results
                    </span>
                    {isSearching && (
                      <span className="text-[10px] text-blue-400 flex items-center gap-1">
                        <Loader2 size={10} className="animate-spin" />
                        Searching all...
                      </span>
                    )}
                  </div>
                  
                  <div className="max-h-72 overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
                    {(searchResults || instantSearchResults)?.length === 0 ? (
                      <div className="text-center py-6">
                        <Search size={24} className="mx-auto text-zinc-600 mb-2" />
                        <p className="text-sm text-zinc-500">No events found</p>
                        <p className="text-xs text-zinc-600 mt-1">Try a different search term</p>
                      </div>
                    ) : (
                      (searchResults || instantSearchResults)?.map(event => {
                        const color = getEventColor(event.id);
                        return (
                          <button
                            key={event.id}
                            onClick={() => handleSearchResultClick(event)}
                            className="w-full text-left px-3 py-2.5 hover:bg-zinc-700/50 rounded-lg text-sm flex items-start gap-3 transition-colors group"
                          >
                            <div className={cn("w-1 h-10 rounded-full flex-shrink-0 mt-0.5", color.bg)} />
                            <div className="flex-1 min-w-0">
                              <div className="text-white font-medium truncate group-hover:text-blue-400 transition-colors">
                                {event.title}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                                <span>{new Date(event.startTime).toLocaleDateString()}</span>
                                {!event.allDay && (
                                  <>
                                    <span>•</span>
                                    <span>{formatTime12(event.startTime)}</span>
                                  </>
                                )}
                              </div>
                              {event.location && (
                                <div className="text-xs text-zinc-600 truncate mt-0.5">
                                  📍 {event.location}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              
              {/* Empty state hint */}
              {!searchQuery.trim() && (
                <div className="mt-3 text-center py-4">
                  <p className="text-xs text-zinc-500">Start typing to search events</p>
                  <p className="text-[10px] text-zinc-600 mt-1">Searches title, location & notes</p>
                </div>
              )}
            </PopoverContent>
          </Popover>
          
          {/* Calendar Sync Button */}
          <button
            onClick={() => setShowConnectionsPanel(true)}
            className={cn(
              "p-2 transition-colors relative",
              connections.length > 0 ? "text-blue-400 hover:text-blue-300" : "text-zinc-400 hover:text-white"
            )}
            title="Link Calendar"
          >
            <Link2 size={18} />
            {connections.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full" />
            )}
          </button>
          
          <button onClick={() => navigate(-1, false)} className="p-2 text-zinc-400 hover:text-white">
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-xs font-medium text-white bg-zinc-700 rounded hover:bg-zinc-600"
          >
            Today
          </button>
          <button onClick={() => navigate(1, false)} className="p-2 text-zinc-400 hover:text-white">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Content area with sidebar */}
      <div className="flex-1 flex min-h-0">
        {/* Main calendar content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Month/Year header */}
          <div className="px-6 py-4 bg-[#1c1c1e]">
            <h1 className="text-3xl font-bold text-white">
              {MONTHS[currentDate.getMonth()]} <span className="text-zinc-400">{currentDate.getFullYear()}</span>
            </h1>
          </div>

          {/* Main content area */}
          <div 
            ref={calendarContainerRef}
            className="flex-1 flex min-h-0"
          >
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-zinc-600 border-t-zinc-300 rounded-full" />
              </div>
            ) : (
              <>
                <div className="flex-1 min-h-0 overflow-hidden">
                  <motion.div
                    key={`${currentDate.getMonth()}-${currentDate.getFullYear()}-${view}`}
                    initial={scrollDirection ? { y: scrollDirection > 0 ? 100 : -100, opacity: 0 } : false}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: scrollDirection > 0 ? -100 : 100, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="h-full"
                  >
                    {view === 'month' && (
                      <FullMonthView
                        currentDate={currentDate}
                        events={finalDisplayEvents}
                        onEventClick={handleEventClick}
                        onDateSelect={handleDateSelect}
                        onQuickCreate={handleQuickCreate}
                      />
                    )}
                    {view === 'day' && (
                      <DayView
                        currentDate={currentDate}
                        events={finalDisplayEvents}
                        onEventClick={handleEventClick}
                      />
                    )}
                    {view === 'week' && (
                      <WeekView
                        currentDate={currentDate}
                        events={finalDisplayEvents}
                        onEventClick={handleEventClick}
                        onDateSelect={handleDateSelect}
                      />
                    )}
                    {view === 'year' && (
                      <div className="flex-1 flex items-center justify-center text-zinc-500">
                        Year view coming soon
                      </div>
                    )}
                  </motion.div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mini calendar sidebar */}
        <div className="w-[220px] p-4 bg-[#151517] border-l border-zinc-800 hidden xl:block">
          <MiniCalendar
            currentDate={currentDate}
            onDateSelect={handleDateSelect}
            events={events}
          />
        </div>
      </div>

      {/* Event Details Popover */}
      <AnimatePresence>
        {popoverEvent && popoverPosition && (
          <EventDetailsPopover
            event={popoverEvent}
            position={popoverPosition}
            color={getEventColor(popoverEvent.id)}
            onClose={closePopover}
            onEdit={handleEditFromPopover}
            onDelete={handleDeleteEvent}
            fromSearch={popoverFromSearch}
          />
        )}
      </AnimatePresence>

      {/* Event Modal */}
      <EventModal
        open={eventModalOpen}
        onClose={() => { setEventModalOpen(false); setSelectedEvent(null); }}
        event={selectedEvent}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        toast={toast}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#2c2c2e] border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Event</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete this event? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-600 text-zinc-300 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Calendar Connections Panel */}
      <AnimatePresence>
        {showConnectionsPanel && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConnectionsPanel(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-[400px] bg-[#1c1c1e] border-l border-zinc-800 z-50 flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
                <div>
                  <h2 className="text-lg font-semibold text-white">Link Calendars</h2>
                  <p className="text-xs text-zinc-500 mt-0.5">Sync your personal calendars</p>
                </div>
                <button
                  onClick={() => setShowConnectionsPanel(false)}
                  className="p-2 text-zinc-400 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Connect New Calendar */}
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Connect a Calendar</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={connectGoogleCalendar}
                      disabled={connectingProvider === 'google'}
                      className="flex flex-col items-center justify-center gap-3 p-6 bg-[#2c2c2e] hover:bg-[#3a3a3c] rounded-xl border border-zinc-700/50 transition-colors disabled:opacity-50"
                    >
                      {connectingProvider === 'google' ? (
                        <Loader2 size={28} className="animate-spin text-zinc-400" />
                      ) : (
                        <img src="https://customer-assets.emergentagent.com/job_3c8d9c45-7bb3-4613-b650-551c11e266fb/artifacts/b341de0b_Google_Symbol_0.svg" alt="Google" className="w-8 h-8" />
                      )}
                      <span className="text-sm text-white font-medium">Google</span>
                    </button>
                    <button
                      onClick={() => toast.info('Apple Calendar coming soon')}
                      className="flex flex-col items-center justify-center gap-3 p-6 bg-[#2c2c2e] hover:bg-[#3a3a3c] rounded-xl border border-zinc-700/50 transition-colors opacity-50 cursor-not-allowed"
                      disabled
                    >
                      <img src="https://customer-assets.emergentagent.com/job_3c8d9c45-7bb3-4613-b650-551c11e266fb/artifacts/f1kicia1_Apple_Logo_0.svg" alt="Apple" className="w-8 h-8 invert" />
                      <span className="text-sm text-white font-medium">Apple</span>
                    </button>
                  </div>
                </div>

                {/* Connected Calendars */}
                {connections.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Connected</h3>
                    <div className="space-y-3">
                      {connections.map((conn) => (
                        <div key={conn.id} className="bg-[#2c2c2e] rounded-xl border border-zinc-700/50 overflow-hidden">
                          <div className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                {conn.provider === 'google' ? (
                                  <img src="https://customer-assets.emergentagent.com/job_3c8d9c45-7bb3-4613-b650-551c11e266fb/artifacts/b341de0b_Google_Symbol_0.svg" alt="Google" className="w-5 h-5" />
                                ) : (
                                  <img src="https://customer-assets.emergentagent.com/job_3c8d9c45-7bb3-4613-b650-551c11e266fb/artifacts/f1kicia1_Apple_Logo_0.svg" alt="Apple" className="w-5 h-5 invert" />
                                )}
                                <div>
                                  <div className="text-sm text-white font-medium">{conn.email}</div>
                                  <div className="text-xs text-zinc-500">
                                    {conn.selectedCalendars?.length || 0} calendar(s) synced
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => syncCalendar(conn.id)}
                                  disabled={syncing}
                                  className="p-1.5 text-zinc-400 hover:text-blue-400 transition-colors disabled:opacity-50"
                                  title="Sync now"
                                >
                                  <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                                </button>
                                <button
                                  onClick={() => {
                                    if (selectedConnection === conn.id) {
                                      setSelectedConnection(null);
                                    } else {
                                      setSelectedConnection(conn.id);
                                      fetchAvailableCalendars(conn.id);
                                    }
                                  }}
                                  className="p-1.5 text-zinc-400 hover:text-white transition-colors"
                                  title="Settings"
                                >
                                  <Settings size={14} />
                                </button>
                              </div>
                            </div>
                            {conn.lastSync && (
                              <div className="mt-2 text-[10px] text-zinc-600">
                                Last synced: {new Date(conn.lastSync).toLocaleString()}
                              </div>
                            )}
                          </div>

                          {/* Calendar Selection */}
                          <AnimatePresence>
                            {selectedConnection === conn.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-zinc-700/50 overflow-hidden"
                              >
                                <div className="p-4 bg-[#252528]">
                                  <div className="text-xs text-zinc-500 mb-3">Select calendars to sync:</div>
                                  {loadingCalendars ? (
                                    <div className="flex items-center justify-center py-4">
                                      <Loader2 size={20} className="animate-spin text-zinc-400" />
                                    </div>
                                  ) : (
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                      {availableCalendars.map((cal) => {
                                        const isSelected = conn.selectedCalendars?.includes(cal.id);
                                        return (
                                          <button
                                            key={cal.id}
                                            onClick={() => {
                                              const newSelection = isSelected
                                                ? conn.selectedCalendars.filter(id => id !== cal.id)
                                                : [...(conn.selectedCalendars || []), cal.id];
                                              updateSelectedCalendars(conn.id, newSelection);
                                            }}
                                            className={cn(
                                              "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors",
                                              isSelected ? "bg-blue-500/20" : "hover:bg-zinc-700/50"
                                            )}
                                          >
                                            <div
                                              className="w-3 h-3 rounded-full flex-shrink-0"
                                              style={{ backgroundColor: cal.color || '#6366f1' }}
                                            />
                                            <span className="text-sm text-white flex-1 truncate">{cal.name}</span>
                                            {isSelected && <Check size={14} className="text-blue-400 flex-shrink-0" />}
                                            {cal.primary && (
                                              <span className="text-[10px] text-zinc-500 bg-zinc-700 px-1.5 py-0.5 rounded">Primary</span>
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  <button
                                    onClick={() => disconnectCalendar(conn.id)}
                                    className="mt-4 w-full py-2 text-xs text-red-400 hover:text-red-300 transition-colors border-t border-zinc-700/50 pt-3"
                                  >
                                    Disconnect Calendar
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Info */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <ExternalLink size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm text-blue-300 font-medium">Two-Way Sync</div>
                      <p className="text-xs text-blue-400/70 mt-1">
                        Events from your linked calendars will appear in this view. 
                        External events are read-only and marked with their source calendar color.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Calendar;
